// Ids for synced entities (biblestandard-api docs/integration/sync.md §2.2): a UUIDv7 for anything the
// reader makes, and a UUIDv5 of "<collection>:<key>" for keyed collections, so two devices that each
// make "the reading position in kjv" name the same entity.

/** The namespace every keyed id is derived in (sync.md §2.2). */
export const SYNC_NAMESPACE = "8c2f3b1e-6a4d-4f0b-9e57-1d2c3b4a5f60";

const hex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
const dashed = (h: string) => `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;

/** UUIDv7 (RFC 9562): 48-bit ms timestamp, then random bits. Sorts by creation time. */
export function uuidv7(now: number = Date.now()): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  let ms = now;
  for (let i = 5; i >= 0; i--) {
    b[i] = ms % 256;
    ms = Math.floor(ms / 256);
  }
  b[6] = (b[6]! & 0x0f) | 0x70;
  b[8] = (b[8]! & 0x3f) | 0x80;
  return dashed(hex(b));
}

/** UUIDv5 (RFC 9562 §5.5): SHA-1 over the namespace's 16 bytes, then the UTF-8 name. */
export async function uuidv5(name: string, namespace: string = SYNC_NAMESPACE): Promise<string> {
  const ns = Uint8Array.from(namespace.replaceAll("-", "").match(/../g)!, (h) => parseInt(h, 16));
  const nameBytes = new TextEncoder().encode(name);
  const input = new Uint8Array(ns.length + nameBytes.length);
  input.set(ns);
  input.set(nameBytes, ns.length);
  const b = new Uint8Array(await crypto.subtle.digest("SHA-1", input)).slice(0, 16);
  b[6] = (b[6]! & 0x0f) | 0x50;
  b[8] = (b[8]! & 0x3f) | 0x80;
  return dashed(hex(b));
}

/** The id of a keyed entity: readingPosition by libraryId, shelf items by libraryId, settings by key. */
export const keyedId = (collection: string, key: string) => uuidv5(`${collection}:${key}`);

/** 8 random bytes as hex: this browser's node id, made once and kept with the local store. */
export function newNodeId(): string {
  const b = new Uint8Array(8);
  crypto.getRandomValues(b);
  return hex(b);
}
